import React, { useState, useEffect } from 'react';
import { WithStyles } from '@material-ui/core/styles';
import { ClassNameMap } from '@material-ui/core/styles/withStyles';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { withStyles, Paper, Box, Tabs, Tab, Dialog, DialogActions, DialogContent, DialogTitle, Button, Typography } from '@material-ui/core';
import CustomDatePicker from '../../../components/CustomDatePicker';
import 'chart.js/auto';
import Dashboard from '../../../layouts/Dashboard';
import { useQuery } from '@apollo/client';
import { ToggleButton, ToggleButtonGroup } from '@material-ui/lab';
import { getDaysPast, getStartOfWeek, getMonthsPast } from '../../../utils/dateUtils';
import ServedOrderSection from '../../Admin/Home/components/ServedOrderSection/index';
import DeliverectOrderSection from '../../Admin/Home/components/DeliverectOrderSection/index';
import TakeawayOrderSection from '../../Admin/Home/components/TakeawayOrderSection/index';
import CombinedOrderSection from '../../Admin/Home/components/CombinedOrderSection';
import styles from '../../Admin/Home/styles';
import { locationIdQuery } from './salesDashboardQueries';

interface Props extends WithStyles<typeof styles>, RouteComponentProps {
  classes: ClassNameMap<string>;
}

type DateRange = {
  startDate: Date | null;
  endDate: Date | null;
};

const SalesDashboard = ({ classes }: Props): React.ReactElement => {
  const [historyFiltered, setHistoryFiltered] = useState<string>('WEEK');
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: getDaysPast(7),
    endDate: new Date(),
  });
  const [customStartDate, setCustomStartDate] = useState<Date | null>(getDaysPast(7));
  const [customEndDate, setCustomEndDate] = useState<Date | null>(new Date());
  const [customDateErrors, setCustomDateErrors] = useState<{ startDate: string | boolean; endDate: string | boolean }>({ startDate: false, endDate: false });
  const [locationId, setLocationId] = useState<string>('');
  const [tab, setTab] = useState(0);
  const [customDialogOpen, setCustomDialogOpen] = useState(false);
  const [localCustomStartDate, setLocalCustomStartDate] = useState<Date | null>(customStartDate);
  const [localCustomEndDate, setLocalCustomEndDate] = useState<Date | null>(customEndDate);
  const [localCustomDateErrors, setLocalCustomDateErrors] = useState<{ startDate: string | boolean; endDate: string | boolean }>({ startDate: false, endDate: false });

  const { data: locData } = useQuery(locationIdQuery);

  useEffect(() => {
    if (locData?.locations?.[0]?.id) {
      setLocationId(locData.locations[0].id);
    }
  }, [locData]);

  useEffect(() => {
    let startDate: Date | null = null;
    let endDate: Date | null = null;
    const today = new Date();
    switch (historyFiltered) {
      case 'ONE':
        startDate = getDaysPast(0);
        endDate = today;
        break;
      case 'WEEK':
        startDate = getStartOfWeek(0);
        endDate = today;
        break;
      case 'MONTH':
        startDate = getMonthsPast(1);
        endDate = today;
        break;
      case '3_MONTH':
        startDate = getMonthsPast(3);
        endDate = today;
        break;
      case 'CUSTOM':
        startDate = customStartDate;
        endDate = customEndDate;
        break;
      default:
        startDate = getDaysPast(7);
        endDate = today;
    }
    setDateRange({ startDate, endDate });
  }, [historyFiltered, customStartDate, customEndDate]);

  const customDateValid =
    historyFiltered !== 'CUSTOM' || (customStartDate && customEndDate && !customDateErrors.startDate && !customDateErrors.endDate && customStartDate <= customEndDate);

  const openCustomDialog = () => {
    setLocalCustomStartDate(customStartDate);
    setLocalCustomEndDate(customEndDate);
    setLocalCustomDateErrors({ startDate: false, endDate: false });
    setCustomDialogOpen(true);
  };

  const handleHistoryChange = (_: unknown, value: string | null) => {
    if (value === 'CUSTOM') {
      openCustomDialog();
    } else if (value) {
      setHistoryFiltered(value);
    }
  };

  const handleApplyCustom = () => {
    setCustomStartDate(localCustomStartDate);
    setCustomEndDate(localCustomEndDate);
    setCustomDateErrors(localCustomDateErrors);
    setHistoryFiltered('CUSTOM');
    setCustomDialogOpen(false);
  };

  const handleCancelCustom = () => {
    setCustomDialogOpen(false);
  };

  return (
    <Dashboard title="Sales Dashboard">
      <Paper className={classes.dateRangeContainer}>
        <Box display="flex" alignItems="center" flexWrap="wrap" mb={3}>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            <Typography variant="subtitle2" style={{ marginBottom: 4 }}>
              Time Range
            </Typography>
            <div className={classes.datePicker}>
              <ToggleButtonGroup size="small" value={historyFiltered} exclusive color="primary" onChange={handleHistoryChange}>
                <ToggleButton classes={{ root: classes.toggleButton }} value="ONE">
                  Today
                </ToggleButton>
                <ToggleButton classes={{ root: classes.toggleButton }} value="WEEK">
                  This week
                </ToggleButton>
                <ToggleButton classes={{ root: classes.toggleButton }} value="MONTH">
                  This month
                </ToggleButton>
                <ToggleButton classes={{ root: classes.toggleButton }} value="3_MONTH">
                  Last 3 months
                </ToggleButton>
                <ToggleButton classes={{ root: classes.toggleButton }} value="CUSTOM" onClick={() => setCustomDialogOpen(true)}>
                  Custom
                </ToggleButton>
              </ToggleButtonGroup>
            </div>
          </div>
        </Box>
        <Dialog open={customDialogOpen} onClose={handleCancelCustom} maxWidth="xs" fullWidth>
          <DialogTitle>Select Custom Date Range</DialogTitle>
          <DialogContent>
            <div className={classes.customDatePickers} style={{ display: 'flex', flexDirection: 'column', alignItems: 'stretch', gap: 16 }}>
              <CustomDatePicker
                small
                label="Start date"
                date={localCustomStartDate}
                allowPastDates
                error={!!localCustomDateErrors.startDate}
                helperText={localCustomDateErrors.startDate !== false ? (localCustomDateErrors.startDate as string) : undefined}
                yearsFromNow={-4}
                handleDateChange={(date: Date) => {
                  setLocalCustomStartDate(date);
                  if (localCustomEndDate && date > localCustomEndDate) {
                    setLocalCustomDateErrors((i) => ({ ...i, startDate: 'Start date must be before end date' }));
                  } else {
                    setLocalCustomDateErrors((i) => ({ ...i, startDate: false }));
                  }
                }}
              />
              <CustomDatePicker
                small
                label="End date"
                date={localCustomEndDate}
                allowPastDates
                error={!!localCustomDateErrors.endDate}
                helperText={localCustomDateErrors.endDate !== false ? (localCustomDateErrors.endDate as string) : undefined}
                yearsFromNow={5}
                handleDateChange={(date: Date) => {
                  setLocalCustomEndDate(date);
                  if (localCustomStartDate && date < localCustomStartDate) {
                    setLocalCustomDateErrors((i) => ({ ...i, endDate: 'End date must be after start date' }));
                  } else {
                    setLocalCustomDateErrors((i) => ({ ...i, endDate: false }));
                  }
                }}
              />
            </div>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCancelCustom} color="secondary">
              Cancel
            </Button>
            <Button
              onClick={handleApplyCustom}
              color="primary"
              disabled={!!localCustomDateErrors.startDate || !!localCustomDateErrors.endDate || !localCustomStartDate || !localCustomEndDate}>
              Apply
            </Button>
          </DialogActions>
        </Dialog>
        <Tabs value={tab} onChange={(_, v) => setTab(v)} indicatorColor="primary" textColor="primary">
          <Tab label="All Orders" />
          <Tab label="Served Orders" />
          <Tab label="Deliverect Orders" />
          <Tab label="Takeaway Orders" />
        </Tabs>
      </Paper>
      {tab === 0 && (
        <Box mb={3}>
          <CombinedOrderSection locationId={locationId} dateRange={dateRange} customDateValid={!!customDateValid} classes={classes} />
        </Box>
      )}
      {tab === 1 && (
        <Box mb={3}>
          <ServedOrderSection locationId={locationId} dateRange={dateRange} customDateValid={!!customDateValid} classes={classes} />
        </Box>
      )}
      {tab === 2 && (
        <Box mt={9}>
          <DeliverectOrderSection locationId={locationId} dateRange={dateRange} customDateValid={!!customDateValid} classes={classes} />
        </Box>
      )}
      {tab === 3 && (
        <Box mb={3}>
          <TakeawayOrderSection locationId={locationId} dateRange={dateRange} customDateValid={!!customDateValid} classes={classes} />
        </Box>
      )}
    </Dashboard>
  );
};

export default withRouter(withStyles(styles)(SalesDashboard));
